/**
 * One file per client, and nothing shared between them.
 *
 * Everything in `src/config/caseDetailsConfig.json` describes the UI and is
 * shared by every client on purpose — a change to the interface should reach
 * all of them. Everything that differs per client is *data*: which backend it
 * talks to, what it calls itself there, which media host serves its images,
 * which tabs its API can actually feed. That lives here, one client per file,
 * so a change to one cannot reach another.
 *
 * The client registry used to be a `client.clients` map inside the shared file,
 * which meant every client's data sat in the same object as everyone else's and
 * a careless edit crossed the boundary silently.
 *
 * Loaded by static import rather than dynamic `import()`. The selection is
 * still made at runtime from the logged-in client — that is what the app reads
 * — but the files are a few hundred bytes each, and making config asynchronous
 * would put a loading state in front of the login page, the request
 * interceptor and the media-URL helper, all of which need an answer
 * synchronously.
 *
 * To add a client: drop in `<key>.json`, register it below, add its dev proxy
 * path to setupProxy.js if it needs one, and register its login URL with
 * scripts/manage-login-config.mjs. Nothing else in the app should need editing.
 */

import bajaj from "./bajaj.json";
import ergo from "./ergo.json";
import ergoProperty from "./ergo_property.json";
import hegic from "./hegic.json";

export const CLIENTS = { bajaj, ergo, hegic, ergo_property: ergoProperty };

/**
 * The clients offered on the login page.
 *
 * Ergo's property line is a client here — its own host, source name and media
 * server — but not a login target: you sign in as Ergo and then choose Motor or
 * Property. `selectable: false` keeps it out of the dropdown while letting the
 * rest of the app resolve it exactly like any other client.
 */
export const SELECTABLE_CLIENTS = Object.fromEntries(
  Object.entries(CLIENTS).filter(([, client]) => client.selectable !== false),
);

export const CLIENT_KEYS = Object.keys(CLIENTS);

/**
 * Every spelling that should resolve to a client, built from the JSON itself.
 *
 * A client is referred to by two names: the registry key, which is the file
 * name ("ergo_property"), and its own `sourceName`, which is what the backend
 * calls it ("ergobalticproperty"). Both end up in localStorage depending on who
 * wrote it last — the login picker writes the key, Layout writes the source
 * name — and a lookup that knew only one of them returned null for the other.
 *
 * Null then reads as "no such client", and every caller falls back to its
 * default: the motor list, the motor media host, the logged-in client's source.
 * That is why choosing Property opened Motor. The config was present the whole
 * time; it was being asked for under the other name.
 */
const BY_ANY_NAME = Object.entries(CLIENTS).reduce((index, [key, client]) => {
  index[key] = client;
  if (client?.sourceName) index[client.sourceName] = client;
  return index;
}, {});

/** The client's own config, or null — never a partially-filled default. */
export const getClient = (key) => (key && BY_ANY_NAME[key]) || null;

/**
 * Resolve a field that differs between development and production.
 *
 * Written as `{ development, production }` in the JSON so the two live next to
 * each other rather than in a conditional somewhere else. A plain string means
 * the same value in both.
 */
export const resolveEnvValue = (value) => {
  if (value == null) return null;
  if (typeof value === "string") return value;
  const env = process.env.NODE_ENV === "development" ? "development" : "production";
  const resolved = value[env];
  return typeof resolved === "string" ? resolved : null;
};

export default CLIENTS;
